import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { ApiError, ConfirmationRequiredError, NetworkError, type Confirmation } from '../api/client';
import type { ConfirmationChoice, Device } from '../api/types';
import { CHOICE_LABEL, ERROR_MESSAGES } from '../lib/labels';
import { time } from '../lib/time';

type Toast = { id: number; tone: 'ok' | 'bad' | 'info'; text: string };

interface FeedbackCtx {
  toast: (text: string, tone?: Toast['tone']) => void;
  ask: (err: ConfirmationRequiredError) => Promise<ConfirmationChoice | null>;
}

const Ctx = createContext<FeedbackCtx | null>(null);

export function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    const base = ERROR_MESSAGES[e.code] ?? e.message;
    if (e.code === 'VALIDATION_ERROR' && Array.isArray(e.details)) {
      const first = e.details[0] as { loc?: unknown[]; message?: string } | undefined;
      if (first?.message) return `${base} ${first.loc?.slice(1).join('.') ?? ''} : ${first.message}`;
    }
    return e.message && e.message !== base && !ERROR_MESSAGES[e.code] ? e.message : base;
  }
  if (e instanceof NetworkError) return 'Service injoignable. Vérifiez la connexion ou le tunnel.';
  return (e as Error)?.message ?? 'Erreur inattendue';
}

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [pending, setPending] = useState<{ err: ConfirmationRequiredError; resolve: (c: ConfirmationChoice | null) => void } | null>(null);
  const seq = useRef(0);

  const toast = useCallback((text: string, tone: Toast['tone'] = 'ok') => {
    const id = ++seq.current;
    setToasts((t) => [...t, { id, tone, text }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), tone === 'bad' ? 8000 : 4000);
  }, []);

  const ask = useCallback(
    (err: ConfirmationRequiredError) => new Promise<ConfirmationChoice | null>((resolve) => setPending({ err, resolve })),
    [],
  );

  const close = (c: ConfirmationChoice | null) => {
    pending?.resolve(c);
    setPending(null);
  };

  return (
    <Ctx.Provider value={{ toast, ask }}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.tone}`}>{t.text}</div>
        ))}
      </div>
      {pending && <ConfirmDialog err={pending.err} onClose={close} />}
    </Ctx.Provider>
  );
}

function ConfirmDialog({ err, onClose }: { err: ConfirmationRequiredError; onClose: (c: ConfirmationChoice | null) => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const qc = useQueryClient();
  const devices = qc.getQueryData<Device[]>(['devices']) ?? [];
  const nameOf = (id: string) => {
    const d = devices.find((x) => x.id === id);
    return d ? d.name || d.client_id : id;
  };
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  const ids = err.info.suspended_device_ids ?? [];
  return (
    <dialog ref={ref} className="dialog" aria-labelledby="confirm-title" onCancel={(e) => { e.preventDefault(); onClose(null); }}>
      <h2 id="confirm-title">Confirmation requise</h2>
      <p>{ERROR_MESSAGES[err.code] && err.code !== 'CONFIRMATION_REQUIRED' ? ERROR_MESSAGES[err.code] : err.message}</p>
      {err.message && ERROR_MESSAGES[err.code] && err.message !== ERROR_MESSAGES[err.code] && <p className="muted small">{err.message}</p>}
      {ids.length > 0 && <p className="small">Radiateurs concernés : <span className="strong">{ids.map(nameOf).join(', ')}</span></p>}
      {err.expiresAt && <p className="small muted">Valable jusqu’à {time(err.expiresAt)}. Rien n’est modifié sans votre choix.</p>}
      <div className="stack">
        {err.choices.map((c) => (
          <button key={c} type="button" className="btn btn-primary" onClick={() => onClose(c)}>{CHOICE_LABEL[c] ?? c}</button>
        ))}
        <button type="button" className="btn" onClick={() => onClose(null)}>Abandonner</button>
      </div>
    </dialog>
  );
}

export function useFeedback() {
  const c = useContext(Ctx);
  if (!c) throw new Error('FeedbackProvider manquant');
  return c;
}

/**
 * Exécute une mutation : gère la confirmation 409 (rejouée avec une nouvelle Idempotency-Key, le même corps
 * et le même If-Match), les messages, et l'invalidation des requêtes concernées.
 */
export function useAction() {
  const { toast, ask } = useFeedback();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);

  const run = useCallback(
    async <T,>(
      fn: (confirmation?: Confirmation) => Promise<T>,
      { success, invalidate = [] }: { success?: string | ((r: T) => string | undefined); invalidate?: QueryKey[] } = {},
    ): Promise<T | undefined> => {
      setBusy(true);
      try {
        let result: T;
        try {
          result = await fn();
        } catch (e) {
          if (!(e instanceof ConfirmationRequiredError) || e.choices.length === 0) throw e;
          const choice = await ask(e);
          if (!choice) {
            toast('Demande abandonnée, rien n’a été modifié.', 'info');
            return undefined;
          }
          result = await fn({ token: e.token, choice });
        }
        const msg = typeof success === 'function' ? success(result) : success;
        if (msg) toast(msg, 'ok');
        return result;
      } catch (e) {
        toast(errorText(e), 'bad');
        return undefined;
      } finally {
        setBusy(false);
        await Promise.all(invalidate.map((k) => qc.invalidateQueries({ queryKey: k })));
      }
    },
    [ask, qc, toast],
  );

  return { run, busy };
}
